import React, { useState, useEffect } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, Clock, Send } from "lucide-react";
import { restaurant } from "../mock";
import { useToast } from "../hooks/use-toast";
import { useLang } from "../i18n/LanguageContext";
import SectionBg from "./SectionBg";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useLang();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [hours, setHours] = useState(restaurant.hours);

  useEffect(() => {
    axios.get(`${API}/hours`)
      .then((res) => { if (Array.isArray(res.data) && res.data.length) setHours(res.data); })
      .catch(() => {});
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: t("contact.errTitle"), description: t("contact.errDesc") });
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setForm({ name: "", email: "", message: "" });
      toast({ title: t("contact.okTitle"), description: t("contact.okDesc") });
    } catch (err) {
      toast({ title: t("contact.failTitle"), description: t("contact.failDesc") });
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-4 py-3 text-[#23201C] outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";

  return (
    <section id="contatti" className="py-24 md:py-32 bg-[#1C1815] relative grain overflow-hidden">
      <SectionBg section="contact" lightText />
      <div className="max-w-7xl mx-auto px-5 md:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <div className="reveal">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-[#C9A24B]" />
              <span className="text-[#C9A24B] tracking-[0.3em] text-xs uppercase">{t("contact.badge")}</span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-tight mb-6">
              {t("contact.title1")} <span className="italic text-[#C9A24B]">{t("contact.highlight")}</span>
            </h2>
            <p className="text-white/70 text-lg leading-relaxed mb-10">{t("contact.subtitle")}</p>

            <div className="space-y-5 mb-10">
              <InfoRow icon={MapPin} title={t("contact.address")}>{restaurant.address}<br />{restaurant.city}</InfoRow>
              <a href={restaurant.phoneHref} className="block"><InfoRow icon={Phone} title={t("contact.phone")} hover>{restaurant.phone}</InfoRow></a>
              <a href={`mailto:${restaurant.email}`} className="block"><InfoRow icon={Mail} title={t("contact.email")} hover>{restaurant.email}</InfoRow></a>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-[2px] p-6">
              <div className="flex items-center gap-2 mb-4">
                <Clock size={17} className="text-[#C9A24B]" />
                <span className="text-white font-medium tracking-wide">{t("contact.hours")}</span>
              </div>
              <ul className="space-y-1.5">
                {hours.map((h) => (
                  <li key={h.dayKey} className="flex justify-between text-sm">
                    <span className="text-white/60">{t(`days.${h.dayKey}`)}</span>
                    {h.closed
                      ? <span className="text-[#c98a6b] uppercase text-xs tracking-wider font-medium">{t("days.closed")}</span>
                      : <span className="text-white/85">{h.time}</span>}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="reveal">
            <form onSubmit={handleSubmit} className="bg-[#FBF7F0] rounded-[2px] p-8 md:p-10 shadow-2xl">
              <h3 className="font-display text-2xl text-[#23201C] mb-7">{t("contact.formTitle")}</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm text-[#5a5347] mb-2 font-medium">{t("contact.name")}</label>
                  <input name="name" value={form.name} onChange={handleChange} className={inputCls} placeholder={t("contact.namePh")} />
                </div>
                <div>
                  <label className="block text-sm text-[#5a5347] mb-2 font-medium">{t("contact.email")}</label>
                  <input type="email" name="email" value={form.email} onChange={handleChange} className={inputCls} placeholder={t("contact.emailPh")} />
                </div>
                <div>
                  <label className="block text-sm text-[#5a5347] mb-2 font-medium">{t("contact.message")}</label>
                  <textarea name="message" value={form.message} onChange={handleChange} rows={4} className={`${inputCls} resize-none`} placeholder={t("contact.messagePh")} />
                </div>
                <button type="submit" disabled={sending}
                  className="w-full flex items-center justify-center gap-2 bg-[#1C1815] hover:bg-[#2a241f] text-[#C9A24B] px-6 py-4 rounded-full font-medium tracking-wide transition-all duration-300 disabled:opacity-60">
                  {sending ? t("contact.sending") : t("contact.submit")}
                  {!sending && <Send size={16} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function InfoRow({ icon: Icon, title, children, hover }) {
  return (
    <div className={`flex items-start gap-4 ${hover ? "group" : ""}`}>
      <div className="mt-0.5 w-11 h-11 rounded-full bg-[#C9A24B]/15 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A24B] transition-colors">
        <Icon size={18} className="text-[#C9A24B] group-hover:text-[#1C1815] transition-colors" />
      </div>
      <div>
        <p className="text-white/50 text-xs uppercase tracking-wider mb-1">{title}</p>
        <p className="text-white/90 leading-snug">{children}</p>
      </div>
    </div>
  );
}